import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFetcher, useLoaderData, useLocation } from "react-router";
import { useCaseApi } from "../api/case/parse/ParseCaseClient";
import { IssueDescriptors } from "../components/analytics/AnalyticsProps";
import { ClaimMatrix } from "../components/analytics/ClaimMatrix";
import { EvMatrix } from "../components/analytics/EvMatrix";
import {
  CostSummary,
  OutcomeSummary,
  OutcomesForParty,
} from "../shared/Analytics";
import { getStages } from "../shared/British";
import { Stage } from "../shared/CaseConfiguration";
import {
  BargainingData,
  Case,
  PartyDescriptors,
  PartyStats,
  Results,
  Revenue,
  SettlementDataOfParty,
  SettlementImpact,
  StoredResults,
} from "../shared/CaseModel";
import { casePrerequisites, Prerequisite } from "../shared/casePrerequisites";

export interface CaseResults {
  getPartyDescriptors(): PartyDescriptors;
  // Stored outcome summaries only — heavy details are fetched on demand via the
  // getOutcomeDetails API (see ParseCaseClient.getOutcomeDetails).
  getOutcomes(): OutcomesForParty<OutcomeSummary>[];
  getEvMatrix(): EvMatrix;
  getSettlementData(): SettlementDataOfParty[];
  getBargainingData(): BargainingData[];
  getRevenueFrom(partyId: string): Revenue | undefined;
  getRevenueTotal(): Revenue | undefined;
}

export interface CaseContextProps extends CaseResults {
  getCase(): Case;
  /**
   * What the calculation needs of the case, and whether each is met. Worked
   * out once per loaded case and read from here by everything that tells the
   * user what is missing, so no two of them can disagree.
   */
  getPrerequisites(): Prerequisite[];
  setCaseTitle(newTitle: string): Promise<void>;
  getStages(): Stage[];
  getIssueDescriptors(): IssueDescriptors;
  getClaimMatrix(): ClaimMatrix;
  getCostSummaryOf(partyId: string): CostSummary;
  variantResultsLoading: boolean;
  getVariantResults(variantId: string): CaseResults | undefined;
  getOfferImpact(offerId: string): SettlementImpact | undefined;
  getPartyStats(partyId: string): PartyStats | undefined;
  /** True while a case mutation is in-flight (server calculating results). */
  isSaving: boolean;
  /**
   * The user may read the case and not change it (see {@link CaseData}). Every
   * control that would change the case is then disabled: it stays in view,
   * since it also shows how the case is configured.
   */
  readOnly: boolean;
  /** @internal Used by useCaseSubmit — not for direct use. */
  _fetcherSubmit: ReturnType<typeof useFetcher>["submit"];
}

export const CaseContext = React.createContext<CaseContextProps>({
  getCase: function () {
    throw new Error("Case context not initialized");
  },
  getPrerequisites: function () {
    throw new Error("Case context not initialized");
  },
  setCaseTitle: function () {
    throw new Error("Case context not initialized");
  },
  getOutcomes: function () {
    throw new Error("Case context not initialized");
  },
  getEvMatrix: function () {
    throw new Error("Case context not initialized");
  },
  getClaimMatrix: function () {
    throw new Error("Case context not initialized");
  },
  getPartyDescriptors: function () {
    throw new Error("Case context not initialized");
  },
  getIssueDescriptors: function () {
    throw new Error("Case context not initialized");
  },
  getStages: function () {
    throw new Error("Case context not initialized");
  },
  getCostSummaryOf: function () {
    throw new Error("Case context not initialized");
  },
  getSettlementData: function () {
    throw new Error("Case context not initialized");
  },
  getBargainingData: function () {
    throw new Error("Case context not initialized");
  },
  variantResultsLoading: false,
  getVariantResults: function () {
    throw new Error("Case context not initialized");
  },
  getOfferImpact: function () {
    throw new Error("Case context not initialized");
  },
  getPartyStats: function () {
    throw new Error("Case context not initialized");
  },
  getRevenueFrom: function () {
    throw new Error("Case context not initialized");
  },
  getRevenueTotal: function () {
    throw new Error("Case context not initialized");
  },
  isSaving: false,
  readOnly: false,
  _fetcherSubmit: function () {
    throw new Error("Case context not initialized");
  },
});

/**
 * The id of the route that loads a case. What stands outside the pages of a
 * case, and so outside its context, reads the loaded case by it.
 */
export const CASE_ROUTE_ID = "case";

/** What the route of a case loads. */
export type CaseData = {
  c: Case;
  /**
   * The access list of the case grants its user no write access: the server
   * refuses them every save and the delete.
   */
  readOnly: boolean;
};

// provides the case context based on case loaded by react-router's loader
export const CaseContextProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const { c, readOnly } = useLoaderData() as CaseData;

  const issueDescriptors = useMemo(() => {
    const descriptors: IssueDescriptors = {};
    c.claims?.forEach((claim) => {
      descriptors[claim.id] = {
        title: claim.title,
        claimant: claim.claimant.name,
      };
      claim.issues?.forEach((issue) => {
        descriptors[issue.id] = {
          title: issue.title,
          claimant: claim.claimant.name,
        };
      });
    });
    return descriptors;
  }, [c.claims]);

  const claimMatrix = useMemo(
    () => new ClaimMatrix(c.results?.opponents ?? {}),
    [c],
  );

  const api = useCaseApi();
  const fetcher = useFetcher();

  // lazy-load variantResults in background (excluded from initial queryCase for performance)
  const [variantResults, setVariantResults] = useState<
    { [variantId: string]: Results } | undefined
  >(undefined);
  const [variantResultsLoading, setVariantResultsLoading] = useState(true);

  // Clear variant results only when navigating to a different case
  const previousCaseId = useRef(c.id);
  useEffect(() => {
    if (previousCaseId.current !== c.id) {
      setVariantResults(undefined);
      previousCaseId.current = c.id;
    }
  }, [c.id]);

  // Fetch variant results — keep stale data visible during refetch
  useEffect(() => {
    if (!c.variants?.bargaining) {
      setVariantResults(undefined);
      setVariantResultsLoading(false);
      return;
    }
    setVariantResultsLoading(true);
    let cancelled = false;
    api
      .queryVariantResults(c.id)
      .then((results) => {
        if (!cancelled) setVariantResults(results);
      })
      .catch((e) => console.warn("Failed to load variant results", e))
      .finally(() => {
        if (!cancelled) setVariantResultsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [c.id, c.updatedAt]);

  const baseCaseResults = useMemo(() => asCaseResults(c.results), [c.results]);

  const prerequisites = useMemo(() => casePrerequisites(c), [c]);

  const value: CaseContextProps = useMemo(
    () => ({
      ...baseCaseResults,
      getCase: () => c,
      getPrerequisites: () => prerequisites,
      setCaseTitle: (newTitle) => api.setTitle(c.id, newTitle),
      getIssueDescriptors: () => issueDescriptors,
      getStages,
      // results
      getClaimMatrix: () => claimMatrix,
      getCostSummaryOf: (partyId) => {
        const summary = c.results?.costSummary[partyId];
        if (summary === undefined)
          throw new Error(`No cost summary available for party '${partyId}'`);
        return summary;
      },
      variantResultsLoading,
      getVariantResults: (variantId: string) => {
        if (!variantResults) return;
        const result = variantResults[variantId];
        return result ? asCaseResults(result) : undefined;
      },
      getOfferImpact: (offerId: string) =>
        c.results?.settlementImpacts?.find(
          (impact) => impact.offerId === offerId,
        ),
      getPartyStats: (partyId) =>
        c.results?.partyStats?.find((stats) => stats.partyId === partyId),
      getRevenueFrom: (partyId) => c.results?.revenue?.perClient[partyId],
      getRevenueTotal: () => c.results?.revenue?.total,
      isSaving: fetcher.state !== "idle",
      readOnly,
      _fetcherSubmit: fetcher.submit,
    }),
    [
      baseCaseResults,
      c,
      prerequisites,
      issueDescriptors,
      claimMatrix,
      variantResults,
      variantResultsLoading,
      api,
      fetcher.state,
      fetcher.submit,
      readOnly,
    ],
  );
  return <CaseContext.Provider value={value}>{children}</CaseContext.Provider>;
};

function asCaseResults(results: StoredResults | undefined): CaseResults {
  const evMatrix = new EvMatrix(results?.evs ?? {});
  const settlementData: SettlementDataOfParty[] = results?.settlementData ?? [];
  return {
    getPartyDescriptors: () => results?.partyDescriptors ?? {},
    getEvMatrix: () => evMatrix,
    getSettlementData: () => settlementData,
    getOutcomes: () => results?.outcomes ?? [],
    // base case has no bargaining data
    getBargainingData: () => results?.bargainingData ?? [],
    getRevenueFrom: (partyId) => results?.revenue?.perClient[partyId],
    getRevenueTotal: () => results?.revenue?.total,
  };
}

export function useCase() {
  return useContext(CaseContext);
}

/**
 * Returns a submit function that triggers a case mutation via the shared
 * fetcher in CaseContextProvider.  Because the fetcher lives in the parent
 * route it is never cleaned up when child routes unmount, so the server
 * request always completes even when the user navigates away.
 *
 * The action URL is automatically set to the caller's current pathname.
 *
 * Nothing is submitted for a case the user may only read. Its controls are
 * disabled, so nothing should get here; a control that was missed would have
 * its save refused by the server, and a refused save ends on the error page.
 */
export function useCaseSubmit() {
  const { _fetcherSubmit, readOnly } = useContext(CaseContext);
  const { pathname } = useLocation();
  return useCallback(
    (
      data: Record<string, string>,
      options: Parameters<typeof _fetcherSubmit>[1],
    ) =>
      readOnly
        ? Promise.resolve()
        : _fetcherSubmit(data, { ...options, action: pathname }),
    [_fetcherSubmit, pathname, readOnly],
  );
}
